import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { pickFeedbackFile } from './pickFeedbackFile';
import { ApiError } from '../../services/api';
import { sendUserFeedback } from '../../services/userFeedbackService';
import {
  colors,
  elevation,
  fontSize,
  fontWeight,
  lineHeight,
  spacing,
} from '../../theme';
import { showToast } from '../../utils/toast';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { formFieldLabelText } from '../forms/formFieldLabelStyle';
import { AlertCircle, Camera, ChevronDown, Paperclip, X } from '../icons';
import {
  dismissFeedbackCapture,
  requestFeedbackCapture,
} from './feedbackCapture';
import { FeedbackFileError, FeedbackFilePayload } from './feedbackFile';
import { useKeyboardHeight } from '../shared/keyboardInset';

const ISSUE_TYPES = ['Bug', 'Enhancement'] as const;
const DESCRIPTION_MAX = 1000;
/** Matches `useResponsive` tablet width. Narrower windows get a slide-up drawer. */
const WIDE_LAYOUT = 1024;

interface SendFeedbackDialogProps {
  visible: boolean;
  onClose: () => void;
}

interface FeedbackDrawerProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  confirmDisabled?: boolean;
  children: React.ReactNode;
}

/**
 * Phone and narrow web: the form slides up to fill the screen. A centered
 * dialog is only used when the window is wide enough for the avatar menu.
 */
const FeedbackDrawer: React.FC<FeedbackDrawerProps> = ({
  visible,
  onClose,
  onConfirm,
  confirmDisabled = false,
  children,
}) => {
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const keyboardHeight = useKeyboardHeight();
  const sheetTravel = Math.max(height, 1);
  const travelRef = useRef(sheetTravel);
  travelRef.current = sheetTravel;
  const slide = useRef(new Animated.Value(sheetTravel)).current;
  const backdrop = useRef(new Animated.Value(0)).current;
  const [shown, setShown] = useState(visible);
  const [isLoading, setIsLoading] = useState(false);
  const [settled, setSettled] = useState(false);
  const wasVisible = useRef(false);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const confirmingRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setIsLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && !wasVisible.current) {
      wasVisible.current = true;
      setSettled(false);
      setShown(true);
      return;
    }
    if (!visible && wasVisible.current) {
      wasVisible.current = false;
      setSettled(false);
      Animated.parallel([
        Animated.timing(slide, {
          toValue: travelRef.current,
          duration: 220,
          useNativeDriver: false,
        }),
        Animated.timing(backdrop, {
          toValue: 0,
          duration: 220,
          useNativeDriver: false,
        }),
      ]).start(({ finished }) => {
        if (finished && !visibleRef.current) {
          setShown(false);
        }
      });
    }
  }, [visible, slide, backdrop]);

  useEffect(() => {
    if (!shown || !visible) {
      return;
    }
    slide.setValue(travelRef.current);
    const animation = Animated.parallel([
      Animated.timing(slide, {
        toValue: 0,
        duration: 280,
        useNativeDriver: false,
      }),
      Animated.timing(backdrop, {
        toValue: 1,
        duration: 280,
        useNativeDriver: false,
      }),
    ]);
    animation.start(({ finished }) => {
      if (finished) {
        setSettled(true);
      }
    });
  }, [shown, visible, slide, backdrop]);

  const handleConfirm = async () => {
    if (confirmingRef.current || !visibleRef.current) {
      return;
    }
    confirmingRef.current = true;
    setIsLoading(true);
    try {
      await onConfirm();
    } catch {
      confirmingRef.current = false;
      setIsLoading(false);
      return;
    }
    confirmingRef.current = false;
    setIsLoading(false);
    onClose();
  };

  if (!shown) {
    return null;
  }

  const sheetHeight = Math.max(0, height - keyboardHeight);

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={isLoading ? undefined : onClose}
      statusBarTranslucent
    >
      <View style={styles.drawerHost}>
        <Animated.View style={[styles.drawerBackdrop, { opacity: backdrop }]}>
          <Pressable
            style={styles.drawerBackdropPress}
            onPress={isLoading || !settled ? undefined : onClose}
            accessibilityLabel="Dismiss send feedback"
          />
        </Animated.View>
        <Animated.View
          testID="send-feedback-drawer"
          style={[
            styles.drawerSheet,
            {
              height: sheetHeight,
              marginBottom: keyboardHeight,
              paddingTop: Math.max(insets.top, spacing.md),
              paddingBottom: Math.max(insets.bottom, spacing.md),
              transform: [{ translateY: slide }],
            },
          ]}
        >
          <View style={styles.drawerMain}>
            <View style={styles.drawerHeader}>
              <View style={styles.drawerClose} />
              <Text style={styles.drawerTitle}>Send feedback</Text>
              <TouchableOpacity
                onPress={onClose}
                disabled={isLoading}
                accessibilityLabel="Close"
                style={styles.drawerClose}
              >
                <X
                  size={20}
                  color={
                    isLoading ? colors.inputBorderNeutral : colors.textSecondary
                  }
                />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.drawerBody}
              keyboardShouldPersistTaps="handled"
            >
              {children}
            </ScrollView>
          </View>
          <View style={styles.drawerFooter}>
            <TouchableOpacity
              testID="confirmation-cancel-btn"
              onPress={onClose}
              disabled={isLoading}
              style={styles.drawerCancel}
            >
              <Text style={styles.drawerCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="confirmation-confirm-btn"
              onPress={handleConfirm}
              disabled={isLoading || confirmDisabled}
              style={styles.drawerSend}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.drawerSendText}>Send</Text>
              )}
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

function toastForFailure(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.code === 'UNAUTHENTICATED') {
      return 'Sign in again to send feedback.';
    }
    if (
      error.status === 403 ||
      error.code === 'IMPERSONATION_READ_ONLY_VIOLATION'
    ) {
      return 'Feedback cannot be sent for this session.';
    }
    if (error.code === 'ISSUE_TYPE_REQUIRED') {
      return 'Choose an issue type.';
    }
    if (error.code === 'ISSUE_TYPE_INVALID') {
      return 'Choose Bug or Enhancement.';
    }
    if (error.code === 'DESCRIPTION_BLANK') {
      return 'Enter a description.';
    }
    if (error.code === 'DESCRIPTION_TOO_LONG') {
      return 'Description must be 1,000 characters or less.';
    }
    if (error.code === 'FILE_TYPE_BLOCKED') {
      return (
        error.message ||
        'This file type is not allowed for security reasons. Please upload documents, images, videos, or audio files instead.'
      );
    }
    if (error.code === 'FILE_TOO_LARGE') {
      return error.message || 'File size exceeds 100MB limit.';
    }
    if (error.code === 'FILE_REJECTED') {
      return 'That file could not be attached.';
    }
  }
  return 'Feedback was not sent. Try again.';
}

function deviceModel(): string | null {
  try {
    const model = DeviceInfo.getModel();
    if (typeof model !== 'string') {
      return null;
    }
    const trimmed = model.trim();
    return trimmed === '' ? null : trimmed;
  } catch {
    return null;
  }
}

function FieldError({
  testID,
  message,
}: {
  testID: string;
  message: string;
}): React.ReactElement {
  return (
    <View
      testID={testID}
      style={styles.errorRow}
      accessibilityRole="text"
      accessibilityLabel={message}
    >
      <AlertCircle size={14} color={colors.error} style={styles.errorRowIcon} />
      <Text style={styles.errorText}>{message}</Text>
    </View>
  );
}

export const SendFeedbackDialog: React.FC<SendFeedbackDialogProps> = ({
  visible,
  onClose,
}) => {
  const { width } = useWindowDimensions();
  const [issueType, setIssueType] = useState('');
  const [issueTypeOpen, setIssueTypeOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<FeedbackFilePayload | null>(null);
  const [screenFile, setScreenFile] = useState<FeedbackFilePayload | null>(
    null,
  );
  const [selecting, setSelecting] = useState(false);
  const [issueTypeError, setIssueTypeError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const attachingRef = useRef(false);
  const sendingRef = useRef(false);
  const [sending, setSending] = useState(false);
  const ownsCaptureRef = useRef(false);
  const aliveRef = useRef(true);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  useEffect(() => {
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!visible) {
      setIssueTypeError(null);
      setDescriptionError(null);
      setIssueTypeOpen(false);
    }
  }, [visible]);

  useEffect(() => {
    if (visible || !ownsCaptureRef.current) {
      return;
    }
    ownsCaptureRef.current = false;
    setSelecting(false);
    dismissFeedbackCapture();
  }, [visible]);

  const handleAttach = async () => {
    if (attachingRef.current) {
      return;
    }
    attachingRef.current = true;
    setAttaching(true);
    try {
      const picked = await pickFeedbackFile();
      if (picked) {
        setFile(picked);
      }
    } catch (error) {
      showToast.error(
        error instanceof FeedbackFileError
          ? error.message
          : 'That file could not be attached.',
      );
    } finally {
      attachingRef.current = false;
      setAttaching(false);
    }
  };

  const handleCapture = () => {
    if (sendingRef.current || attachingRef.current || ownsCaptureRef.current) {
      return;
    }
    ownsCaptureRef.current = true;
    setSelecting(true);
    requestFeedbackCapture({
      onDone: captured => {
        ownsCaptureRef.current = false;
        if (!aliveRef.current || !visibleRef.current) {
          dismissFeedbackCapture();
          return;
        }
        setScreenFile(captured);
        setSelecting(false);
        dismissFeedbackCapture();
      },
      onDismiss: () => {
        ownsCaptureRef.current = false;
        setSelecting(false);
        dismissFeedbackCapture();
      },
    });
  };

  const handleSend = async () => {
    if (sendingRef.current) {
      throw new Error('send-in-flight');
    }
    if (attachingRef.current) {
      showToast.error('Wait for the file to finish attaching.');
      throw new Error('attaching');
    }
    sendingRef.current = true;
    setSending(true);
    try {
      const trimmedDescription = description.trim();
      if (issueType !== 'Bug' && issueType !== 'Enhancement') {
        setIssueTypeOpen(false);
        setIssueTypeError('Choose an issue type.');
        showToast.error('Choose an issue type.');
        throw new Error('issue-type');
      }
      setIssueTypeError(null);
      if (trimmedDescription === '') {
        setIssueTypeOpen(false);
        setDescriptionError('Enter a description.');
        showToast.error('Enter a description.');
        throw new Error('description');
      }
      if (trimmedDescription.length > DESCRIPTION_MAX) {
        setIssueTypeOpen(false);
        setDescriptionError('Description must be 1,000 characters or less.');
        showToast.error('Description must be 1,000 characters or less.');
        throw new Error('description-length');
      }
      setDescriptionError(null);

      const toastId = showToast.loading('Sending feedback');
      try {
        await sendUserFeedback({
          subject: issueType,
          description: trimmedDescription,
          platform: Platform.OS,
          deviceModel: deviceModel(),
          files: [file, screenFile].filter(
            (item): item is FeedbackFilePayload => item !== null,
          ),
        });
        showToast.dismiss(toastId);
        showToast.success('Thank you. Your feedback has been sent.');
        setIssueType('');
        setIssueTypeOpen(false);
        setDescription('');
        setFile(null);
        setScreenFile(null);
      } catch (error) {
        showToast.dismiss(toastId);
        const message = toastForFailure(error);
        if (error instanceof ApiError) {
          if (
            error.code === 'ISSUE_TYPE_REQUIRED' ||
            error.code === 'ISSUE_TYPE_INVALID'
          ) {
            setIssueTypeOpen(false);
            setIssueTypeError(message);
          }
          if (
            error.code === 'DESCRIPTION_BLANK' ||
            error.code === 'DESCRIPTION_TOO_LONG'
          ) {
            setIssueTypeOpen(false);
            setDescriptionError(message);
          }
        }
        showToast.error(message);
        throw error;
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const renderFields = () => (
    <View style={styles.fields}>
      <Text style={styles.intro}>
        Choose Bug or Enhancement, describe it, and send. A file is optional.
      </Text>
      <View style={[styles.field, issueTypeOpen && styles.issueTypeStack]}>
        <Text style={styles.label}>
          Issue Type
          <Text style={styles.required}> *</Text>
        </Text>
        <View>
          <View
            style={[
              styles.issueTypeField,
              issueTypeOpen ? styles.issueTypeFieldOpen : null,
            ]}
          >
            <TouchableOpacity
              testID="send-feedback-issue-type"
              accessibilityRole="button"
              accessibilityLabel="Issue Type"
              accessibilityState={{ expanded: issueTypeOpen }}
              style={[
                styles.issueType,
                issueTypeOpen && styles.issueTypeJoined,
                issueTypeError ? styles.inputError : null,
              ]}
              onPress={() => setIssueTypeOpen(open => !open)}
            >
              <Text
                style={issueType === '' ? styles.placeholder : styles.value}
              >
                {issueType === '' ? 'Choose an issue type' : issueType}
              </Text>
              <View style={issueTypeOpen ? styles.chevronOpen : undefined}>
                <ChevronDown size={16} color="#666" />
              </View>
            </TouchableOpacity>
            {issueTypeOpen ? (
              <View
                style={[styles.menu, issueTypeError ? styles.inputError : null]}
              >
                {ISSUE_TYPES.map(option => (
                  <TouchableOpacity
                    key={option}
                    testID={`send-feedback-issue-type-${option}`}
                    accessibilityRole="button"
                    accessibilityLabel={option}
                    style={styles.option}
                    onPress={() => {
                      setIssueType(option);
                      setIssueTypeError(null);
                      setIssueTypeOpen(false);
                    }}
                  >
                    <Text style={styles.value}>{option}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : null}
          </View>
          {issueTypeError && !issueTypeOpen ? (
            <FieldError
              testID="send-feedback-issue-type-error"
              message={issueTypeError}
            />
          ) : null}
        </View>
      </View>
      <View style={styles.field}>
        <View style={styles.descriptionHeader}>
          <View style={styles.descriptionTitle}>
            <Text style={styles.label}>
              Description
              <Text style={styles.required}> *</Text>
            </Text>
            <Text style={styles.attachmentTip}>
              One file, up to 100 MB. Documents, images, videos, and audio.
            </Text>
          </View>
          <TouchableOpacity
            testID="send-feedback-attach"
            accessibilityRole="button"
            accessibilityLabel="Attach a file"
            style={styles.paperclip}
            onPress={handleAttach}
            disabled={attaching || sending}
          >
            <Paperclip size={18} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
        <View>
          <TextInput
            testID="send-feedback-description"
            style={[
              styles.input,
              styles.description,
              descriptionError ? styles.inputError : null,
            ]}
            value={description}
            onChangeText={value => {
              setDescription(value);
              if (
                value.trim() !== '' &&
                value.trim().length <= DESCRIPTION_MAX
              ) {
                setDescriptionError(null);
              }
            }}
            onFocus={() => setIssueTypeOpen(false)}
            multiline
            maxLength={DESCRIPTION_MAX}
            accessibilityLabel="Description"
          />
          <View style={styles.charCountRow}>
            <TouchableOpacity
              testID="send-feedback-capture"
              accessibilityRole="button"
              accessibilityLabel="Capture the screen"
              style={styles.captureButton}
              onPress={handleCapture}
              disabled={attaching || sending}
            >
              <Camera size={16} color={colors.primary} />
              <Text style={styles.captureButtonText}>Capture the screen</Text>
            </TouchableOpacity>
            <Text
              testID="send-feedback-char-count"
              style={styles.charCount}
              accessibilityLabel={`${description.length} of ${DESCRIPTION_MAX} characters`}
            >
              {description.length} / {DESCRIPTION_MAX}
            </Text>
          </View>
          {descriptionError ? (
            <FieldError
              testID="send-feedback-description-error"
              message={descriptionError}
            />
          ) : null}
          {file ? (
            <View style={styles.fileChip}>
              <Text style={styles.fileName} numberOfLines={1}>
                {file.fileName}
              </Text>
              <TouchableOpacity
                testID="send-feedback-remove-file"
                accessibilityRole="button"
                accessibilityLabel="Remove file"
                onPress={() => {
                  if (!sending) {
                    setFile(null);
                  }
                }}
                disabled={sending}
                style={styles.remove}
              >
                <X size={14} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
          ) : null}
          {screenFile ? (
            <View style={styles.fileChip}>
              <Text style={styles.fileName} numberOfLines={1}>
                {screenFile.fileName}
              </Text>
              <TouchableOpacity
                testID="send-feedback-remove-capture"
                accessibilityRole="button"
                accessibilityLabel="Remove screen capture"
                onPress={() => {
                  if (!sendingRef.current) {
                    setScreenFile(null);
                  }
                }}
                disabled={sending}
                style={styles.remove}
              >
                <X size={14} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );

  if (selecting) {
    return null;
  }

  if (width < WIDE_LAYOUT) {
    return (
      <FeedbackDrawer
        visible={visible}
        onClose={onClose}
        onConfirm={handleSend}
        confirmDisabled={attaching || sending}
      >
        {renderFields()}
      </FeedbackDrawer>
    );
  }

  return (
    <ConfirmationDialog
      visible={visible}
      onClose={onClose}
      onConfirm={handleSend}
      title="Send feedback"
      message=""
      confirmText="Send"
      cancelText="Cancel"
      cancelButtonStyle="outline"
      closeChrome="plain"
      confirmDisabled={attaching || sending}
      showWarningIcon={false}
    >
      {renderFields()}
    </ConfirmationDialog>
  );
};

const styles = StyleSheet.create({
  fields: {
    width: '100%',
    flexDirection: 'column',
    gap: spacing.formFieldGroupGap,
  },
  field: {
    width: '100%',
    flexDirection: 'column',
    gap: spacing.fieldVerticalGap,
  },
  intro: {
    fontSize: fontSize.subheading,
    lineHeight: lineHeight.body,
    color: colors.textSecondary,
  },
  label: {
    ...formFieldLabelText,
  },
  required: {
    color: colors.error,
  },
  issueTypeStack: {
    zIndex: 30,
    elevation: 24,
  },
  issueTypeField: {
    position: 'relative',
  },
  issueTypeFieldOpen: {
    zIndex: 2,
  },
  issueType: {
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    paddingVertical: spacing.formControlSingleLinePaddingVertical,
    backgroundColor: colors.backgroundForm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: spacing.formControlHeight,
    ...Platform.select({
      web: {
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        ':focus': {
          outlineWidth: 0,
          boxShadow: 'none',
        },
      },
      default: {},
    }),
  },
  inputError: {
    borderColor: colors.inputBorderCritical,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  issueTypeJoined: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 30,
    backgroundColor: colors.backgroundForm,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: colors.inputBorderNeutral,
    borderBottomLeftRadius: spacing.formDropdownBorderRadius,
    borderBottomRightRadius: spacing.formDropdownBorderRadius,
    ...Platform.select({
      web: elevation.dropdownPanel,
      default: elevation.popover,
    }),
  },
  input: {
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    paddingVertical: spacing.formTextAreaPaddingVertical,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    backgroundColor: colors.backgroundForm,
  },
  placeholder: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.inputPlaceholder,
  },
  value: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
  },
  option: {
    minHeight: spacing.formDropdownOptionMinHeight,
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    justifyContent: 'center',
    backgroundColor: colors.backgroundForm,
  },
  descriptionHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  descriptionTitle: {
    flex: 1,
    gap: spacing.xs,
  },
  attachmentTip: {
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.charCount,
    color: colors.textSecondary,
  },
  description: {
    minHeight: Platform.select({
      web: spacing.formTextAreaMinHeightWeb,
      default: spacing.formTextAreaMinHeightNative,
    }),
    textAlignVertical: 'top',
  },
  paperclip: {
    width: spacing.formControlHeight,
    height: spacing.formControlHeight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  charCountRow: {
    width: '100%',
    marginTop: spacing.charCountMarginTop,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  charCount: {
    flexShrink: 0,
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.charCount,
    color: colors.textSecondary,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: spacing.xs,
    gap: spacing.formInlineFeedbackGap,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    flex: 1,
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.charCount,
    color: colors.error,
  },
  captureButton: {
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  captureButtonText: {
    flexShrink: 1,
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.primary,
    fontWeight: fontWeight.semibold,
    includeFontPadding: false,
  },
  fileChip: {
    marginTop: spacing.formFieldStackedActionGap,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingLeft: spacing.sm,
    paddingRight: spacing.xs,
    paddingVertical: spacing.xs,
    borderRadius: spacing.lg,
    backgroundColor: colors.backgroundSecondary,
  },
  fileName: {
    flexShrink: 1,
    fontSize: fontSize.formLabel,
    color: colors.textPrimary,
  },
  remove: {
    width: spacing.lg + spacing.sm,
    height: spacing.lg + spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  drawerHost: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  drawerBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  drawerBackdropPress: {
    flex: 1,
  },
  drawerSheet: {
    alignSelf: 'stretch',
    backgroundColor: colors.backgroundForm,
    paddingHorizontal: spacing.screenPaddingHorizontal,
  },
  drawerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  drawerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: fontSize.sectionTitle,
    lineHeight: lineHeight.sectionTitle,
    fontWeight: fontWeight.semibold,
    color: colors.textPrimary,
  },
  drawerClose: {
    width: spacing.formControlHeight,
    height: spacing.formControlHeight,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
  drawerMain: {
    flex: 1,
    flexDirection: 'column',
    gap: spacing.formFieldGroupGap,
  },
  drawerBody: {
    flex: 1,
  },
  drawerFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.md,
    paddingTop: spacing.formFieldGroupGap,
  },
  drawerCancel: {
    borderWidth: 1,
    borderColor: colors.borderMedium,
    backgroundColor: colors.backgroundPrimary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    borderRadius: spacing.formControlBorderRadius,
  },
  drawerCancelText: {
    color: colors.textPrimary,
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
  },
  drawerSend: {
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.primary,
  },
  drawerSendText: {
    fontSize: fontSize.formLabel,
    color: colors.textLight,
    fontWeight: fontWeight.semibold,
  },
});
